import { useModalContext } from '@providers/ModalProvider';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchBillingAccountTierRatesHistoryQueryFn } from 'app/queries/billing/createFetchBillingAccountTierRatesHistoryQueryFn';

type TProps = {
  billingAccountId: string;
  billingAccountName: string;
  orgName: string;
};

export const AdminBillingAccountRateHistoryModal = ({ billingAccountId, billingAccountName, orgName }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();

  const historyQuery = useQuery({
    queryKey: billingKeys.tierRatesHistoryByAccount(billingAccountId),
    queryFn: createFetchBillingAccountTierRatesHistoryQueryFn(call),
  });
  const billingAccountRatesHistory = historyQuery.data;

  return (
    <ModalWrapper width="400px">
      {historyQuery.isLoading && <AbsoluteSpinner overlay />}

      <ModalTitle>
        Billing Account {billingAccountName} ({billingAccountId} - {orgName}) Tier Rates History
      </ModalTitle>

      {historyQuery.isError ? <ErrorMessage error="Failed to load the tier rates history. Please try again." /> : null}

      <div className="flex flex-col gap-[10px]">
        {billingAccountRatesHistory?.map((rate) => (
          <div
            key={rate.id}
            className="flex items-center justify-between text-[12px] font-medium leading-[100%] text-[var(--color-text-muted)]"
          >
            <div>Old Tier Rate: {Number(rate.oldTierRate)}</div>
            <div>New Tier Rate: {Number(rate.newTierRate)}</div>
            <div>Changed at: {new Date(rate.changedAt).toLocaleString()}</div>
            <div>Changed by: {rate.user ? `${rate.user.name} [${rate.user.email}]` : rate.changedBy}</div>
          </div>
        ))}
      </div>

      <div className="mt-[20px] flex justify-end gap-[10px]">
        <OutlinedButton className="w-[130px]" onClick={closeModal}>
          Close
        </OutlinedButton>
      </div>
    </ModalWrapper>
  );
};
